<docs>
A small status marker: a coloured dot, or a count badge when given `count`. There is no variant
prop, the shape follows `count`: set, a 16px badge that grows with the text; unset, an 8px dot (6px
at `size="sm"`). Display only, no slot, no events.

## Notes

- Commonly passed into the `#indicator` slot of ZoneButton / ZoneIconButton / ZoneAvatar for a
  corner badge, and used by ZoneTabs for a tab's badge.
- The badge is a circle at one digit and a pill beyond it: its min-width equals its 16px height, so
  a single digit fills a round badge and two or more push it wider. Do not raise `px-1`: one digit
  only fits inside the 16px floor because the padding is 4px a side.
- The badge width is uncapped (Figma caps it at 29px with an ellipsis), so a long count stays
  readable rather than clipping. Keep counts short yourself; "99+" is the intended ceiling.
- Neither shape can be squeezed by its parent (`shrink-0`), so a badge dropped into a flex box laid
  out for the dot grows out of that box instead of being crushed. Both set `box-border` themselves.
- `yellow` is not in the Figma set (Red/Orange/Blue/Green/Gray); it exists for parity with the Ruby
  Zone::Indicator (team-payments balance breakdown). Prefer a Figma colour for new work. `primary`
  is Figma's "Blue".

See also ZonePill for a labelled tag/chip.

Figma: [Indicator](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=4209-43208)
</docs>

<template>
  <div
    :class="isCounter ? counterClasses : dotClasses"
    data-testid="indicator"
  >{{ count }}</div>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';

const COLOR_CLASSES = {
  red: 'bg-red-500 text-white',
  orange: 'bg-orange-400 text-white',
  primary: 'bg-primary-700 text-white',
  green: 'bg-green-400 text-white',
  gray: 'bg-gray-200 text-gray-800',
  yellow: 'bg-yellow-200 text-gray-800',
} as const;

export type IndicatorColor = keyof typeof COLOR_CLASSES;

const COLOR_OPTIONS = Object.keys(COLOR_CLASSES) as IndicatorColor[];

const SIZES = ['sm', 'base'] as const;

export type IndicatorSize = (typeof SIZES)[number];

// DOT ONLY — the count badge is one height at every size. `base` is the 8px dot Figma draws on
// its own; `sm` is the 6px one an avatar's corner needs, where an 8px dot on a 24px avatar reads
// as a third of the face.
const DOT_SIZE_CLASSES: Record<IndicatorSize, string[]> = {
  sm: ['w-1.5', 'h-1.5'],
  base: ['w-2', 'h-2'],
};
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneIndicator' });

const props = defineProps({
  /** The badge text ("8", "99+", 12). Unset or empty renders the dot instead. */
  count: {
    type: [String, Number] as PropType<string | number | null>,
    default: null,
  },
  /** Figma calls `primary` "Blue"; `yellow` is not in the Figma set. */
  color: {
    type: String as PropType<IndicatorColor>,
    default: 'primary',
    validator: (v: string) => COLOR_OPTIONS.includes(v as IndicatorColor),
  },
  /**
   * Adds a white ring, for overlaying on a coloured surface. Drawn inside the box, so the footprint
   * stays 8px / 16px either way.
   */
  border: {
    type: Boolean,
    default: false,
  },
  /**
   * Dot only: `sm` is 6px, `base` 8px. The count badge is one height at every size. ZoneAvatar
   * drops to `sm` on its two small sizes, where an 8px dot reads as a third of the face.
   */
  size: {
    type: String as PropType<IndicatorSize>,
    default: 'base',
    validator: (v: string) => (SIZES as readonly string[]).includes(v),
  },
});

const commonClasses = computed<string[]>(() => {
  // Two load-bearing classes here, both about geometry rather than looks:
  //
  // `shrink-0` — both shapes are routinely dropped into a flex box sized for the DOT and
  //   nothing else (ZoneButton's corner wrapper is a literal `h-2 w-2`), where a default
  //   `flex-shrink: 1` squeezed the count badge down to its min-width and let anything longer
  //   spill out of the pill. Nothing here should be compressible by its parent: a squashed
  //   dot is an ellipse and a squashed badge is broken.
  // `box-border` — the round badge and the 8px bordered dot both need min-width/width to
  //   INCLUDE their padding and border. That normally arrives via `box-sizing: border-box` in
  //   the scoped preflight, but this component (like the other leaf primitives) carries no
  //   `w-tw` of its own, so outside a `.w-tw` ancestor it would fall back to content-box and
  //   a one-digit badge would be 24px wide again. State it locally instead of depending on
  //   where the thing is mounted.
  const classes = [
    COLOR_CLASSES[props.color],
    'box-border', 'shrink-0', 'rounded-full', 'text-xs', 'flex', 'items-center', 'justify-center',
  ];

  if (props.border) {classes.push('border', 'border-white');}

  return classes;
});

const dotClasses = computed<string[]>(() => [...DOT_SIZE_CLASSES[props.size], ...commonClasses.value]);

// min-width EQUALS the height, so a single digit is a 16px circle rather than the 18x16
// oval an 18px min-width gave it. Past one digit the content itself drives the width and the
// badge grows into a pill: `px-1` is the only padding, kept at 4px a side precisely because
// one digit (~7px at text-xs) plus 8px of padding still fits inside the 16px floor. Widen the
// padding and the circle becomes an oval again. `whitespace-nowrap` keeps a long count on one
// line instead of wrapping inside a 16px-tall box.
const counterClasses = computed<string[]>(() => [
  'min-w-4', 'h-4', 'px-1', 'text-xs', 'whitespace-nowrap', ...commonClasses.value,
]);

/** COUNT BADGE when there is a count to show, DOT otherwise. */
const isCounter = computed<boolean>(() => props.count !== null && props.count !== '');
</script>
